import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const UnifiedContextStrip = withListOverflow(StrategyContextListOwner);
// src/features/plan/UnifiedContextStrip.jsx
//
// MARKET-PLAN-STRATEGY-POLICY-RESTRUCTURE: Two stacked independent cards
// (Idea B). Strategy card above, Policy card below. Each is a self-contained
// accordion with its own header, body, and footer. The two expand states are
// fully independent — collapsing one never affects the other.
//
// Props:
//   directives      {Array}    — list of directive objects from getDirectives()
//   canEdit         {boolean}  — show write controls in strategy panel
//   onEditDirective {function} — called with (directive|null); null = new
//   baseline        {object|null} — baseline from getPolicyBaseline()
//   overrideCount   {number}   — count of offices with per-office overrides
//   isAdmin         {boolean}  — show "Manage in Admin →" in policy card footer
//   masterExpanded  {object|null} — {expanded:bool, seq:number} expand-all
//   density         {string}   — "comfortable"|"compact" for directive rows
//   markets         {Array}    — market list for name resolution in directives

import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { PlanExpandCaret } from "./PlanExpandCaret.jsx";
import {
  isActNow,
  DIRECTIVE_TYPE_LABELS,
  DIRECTIVE_TIMEFRAME_LABELS,
  DIRECTIVE_STATUS_LABELS,
} from "./StrategyBand.jsx";
import {
  fmtRtoDays,
  fmtDeskRatio,
  fmtHotelingPct,
  fmtSfFloor,
  fmtHcBuffer,
  fmtConfGiveback,
  fmtTeamBlockWaste,
  fmtEffectiveDate,
} from "./PolicyBand.jsx";

const SESSION_KEY_STRATEGY = "plan:unifiedStripStrategyOpen";
const SESSION_KEY_POLICY   = "plan:unifiedStripPolicyOpen";

// Default to expanded: null (first load) or "true" both mean open.
// Only an explicit "false" means closed.
function readSession(key) {
  try { return sessionStorage.getItem(key) !== "false"; } catch { return true; }
}

// ─── Timeframe sort order ─────────────────────────────────────────────────────
const TIMEFRAME_ORDER = ["now", "lt_1yr", "1_2yr", "2_3yr", "3_plus_yr"];

// ─── Active / inactive status split ──────────────────────────────────────────
const ACTIVE_STATUSES = new Set(["aspiration", "in_review", "approved", "in_motion"]);

// ─── Sort helpers ─────────────────────────────────────────────────────────────

function sortDirectives(list) {
  return [...list].sort((a, b) => {
    const tA = TIMEFRAME_ORDER.indexOf(a.timeframe);
    const tB = TIMEFRAME_ORDER.indexOf(b.timeframe);
    const tOrd = (tA === -1 ? 999 : tA) - (tB === -1 ? 999 : tB);
    if (tOrd !== 0) return tOrd;
    return (a.title ?? "").localeCompare(b.title ?? "");
  });
}

// ─── Type colors ──────────────────────────────────────────────────────────────

const TYPE_STYLE = {
  hub_designation:      { color: T.teal,         bg: T.tealSoftBg },
  exit:                 { color: T.ragRed,        bg: T.ragRedBg   },
  new_market:           { color: T.blue,          bg: T.blueSoft   },
  relocate_upgrade:     { color: T.purple,        bg: T.purpleBg   },
  capability_migration: { color: T.amberDark,     bg: T.utilAmber  },
};

const STATUS_STYLE = {
  aspiration: { color: T.mid,           bg: T.lightGray },
  in_review:  { color: T.mid,           bg: T.lightGray },
  approved:   { color: T.utilGreenText, bg: T.utilGreen },
  in_motion:  { color: T.blue,          bg: T.blueSoft  },
  complete:   { color: T.utilGreenText, bg: T.utilGreen },
  cancelled:  { color: T.light,         bg: T.lightGray },
};

// ─── Shared chips ─────────────────────────────────────────────────────────────

function TypeChip({ type }) {
  const style = TYPE_STYLE[type] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{
      display:      "inline-flex",
      alignItems:   "center",
      borderRadius: 6,
      padding:      "2px 10px",
      fontSize:     T.fsXs,
      fontWeight:   T.fwSemibold,
      whiteSpace:   "nowrap",
      color:        style.color,
      background:   style.bg,
    }}>
      {DIRECTIVE_TYPE_LABELS[type] ?? type}
    </span>
  );
}

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{
      display:      "inline-flex",
      alignItems:   "center",
      borderRadius: 6,
      padding:      "2px 10px",
      fontSize:     T.fsXs,
      fontWeight:   T.fwSemibold,
      whiteSpace:   "nowrap",
      color:        style.color,
      background:   style.bg,
    }}>
      {DIRECTIVE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─── Markets cell text ────────────────────────────────────────────────────────

function marketsCellText(directive, nameMap = new Map()) {
  if (!directive.markets || directive.markets.length === 0) return "—";
  return directive.markets
    .map((m) => {
      const name = nameMap.get(m.marketId.toLowerCase()) ?? m.marketId.toUpperCase();
      return m.prospectLabel ? `${name} → ${m.prospectLabel}` : name;
    })
    .join(", ");
}

// ─── Strategy card body (no sub-header) ──────────────────────────────────────

function StrategyPanel({ directives, canEdit, onEditDirective, density = "comfortable", nameMap = new Map() }) {
  const [showInactive, setShowInactive] = useState(false);

  const active   = sortDirectives(directives.filter((d) => ACTIVE_STATUSES.has(d.status)));
  const inactive = directives.filter((d) => !ACTIVE_STATUSES.has(d.status));
  const completeCount  = inactive.filter((d) => d.status === "complete").length;
  const cancelledCount = inactive.filter((d) => d.status === "cancelled").length;
  const visibleRows = showInactive ? [...active, ...inactive] : active;

  const thStyle = {
    fontSize:      T.fsXxs,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color:         "var(--v2-mid)",
    fontWeight:    T.fwBold,
    padding:       "10px 14px",
    textAlign:     "left",
    background:    T.lightGray,
    borderBottom:  "1px solid var(--v2-line)",
    whiteSpace:    "nowrap",
  };
  const tdStyle = {
    fontSize:      T.fsSm,
    color:         T.dark,
    padding:       density === "compact" ? "5px 12px" : "9px 12px",
    borderBottom:  `1px solid ${T.lightGray}`,
    verticalAlign: "middle",
  };

  return (
    <>
      <div
        data-testid="strategy-band-table"
        style={{ maxHeight: 340, overflowY: "auto" }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsXs }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: "28%", textAlign: "left" }}>Directive</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Type</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Markets</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Timeframe</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((d) => (
              <tr
                key={d.id}
                data-testid={`directive-row-${d.id}`}
                style={{ cursor: "pointer" }}
                onClick={() => onEditDirective(d)}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <td style={{ ...tdStyle, fontWeight: T.fwSemibold }}>
                  <span>{d.title}</span>
                  {isActNow(d) && (
                    <span
                      data-testid={`act-now-marker-${d.id}`}
                      style={{
                        marginLeft: 6,
                        fontSize:   T.fsXxs,
                        fontWeight: T.fwSemibold,
                        color:      T.ragRed,
                        whiteSpace: "nowrap",
                      }}
                    >
                      ● Act now
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  <TypeChip type={d.type} />
                </td>
                <td style={{ ...tdStyle, color: T.mid }}>
                  {marketsCellText(d, nameMap)}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {DIRECTIVE_TIMEFRAME_LABELS[d.timeframe] ?? d.timeframe}
                </td>
                <td style={tdStyle}>
                  <StatusBadge status={d.status} />
                </td>
              </tr>
            ))}
            {active.length === 0 && inactive.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: T.light }}>
                  No directives yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Strategy card footer: inactive toggle left, + Add directive right */}
      <div style={{
        padding:        "9px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        borderTop:      (active.length + inactive.length) > 0 ? `1px solid ${T.border}` : "none",
      }}>
        <span>
          {inactive.length > 0 && (
            <button
              type="button"
              data-testid="strategy-band-inactive-toggle"
              onClick={(e) => { e.stopPropagation(); setShowInactive((v) => !v); }}
              style={{
                background:     "none",
                border:         "none",
                padding:        0,
                cursor:         "pointer",
                color:          T.mid,
                fontSize:       T.fsXs,
                fontWeight:     T.fwSemibold,
                fontFamily:     "inherit",
                textDecoration: "underline",
                whiteSpace:     "nowrap",
              }}
            >
              {`Completed (${completeCount}) · Cancelled (${cancelledCount}) — ${showInactive ? "hide" : "show"}`}
            </button>
          )}
          {inactive.length === 0 && (
            <span>Directives drive the per-market Strategy column below and feed Recommendations.</span>
          )}
        </span>
        {canEdit && (
          <button
            type="button"
            data-testid="strategy-band-add-directive"
            onClick={(e) => { e.stopPropagation(); onEditDirective(null); }}
            style={{
              background: "none",
              border:     "none",
              padding:    0,
              cursor:     "pointer",
              color:      "var(--v2-accent)",
              fontSize:   T.fsSm,
              fontWeight: T.fwSemibold,
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            + Add directive
          </button>
        )}
      </div>
    </>
  );
}

// ─── Policy card body (no sub-header; admin link in footer) ───────────────────

const PARAM_ROWS = [
  { label: "Days in office / mo",     key: "rtoDays",           fmt: fmtRtoDays        },
  { label: "Desk sharing",            key: "deskRatio",         fmt: fmtDeskRatio      },
  { label: "Hoteling / visitor hold", key: "hotelingPct",       fmt: fmtHotelingPct    },
  { label: "Conf-room give-back",     key: "confGivebackPct",   fmt: fmtConfGiveback   },
  { label: "Team-block waste",        key: "teamBlockWastePct", fmt: fmtTeamBlockWaste },
  { label: "Density floor",           key: "sfFloor",           fmt: fmtSfFloor        },
  { label: "Target buffer",           key: "hcBufferPct",       fmt: fmtHcBuffer       },
];

function KnobCell({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:    T.white,
        border:        `1px solid ${T.policyBandCellBorder}`,
        borderRadius:  9,
        padding:       "10px 14px",
        display:       "flex",
        flexDirection: "column",
        gap:           4,
      }}
    >
      <span style={{
        fontSize:      T.fsXxs,
        color:         T.light,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight:    T.fwBold,
      }}>
        {label}
      </span>
      <span style={{
        fontSize:   T.fsLg,
        color:      T.dark,
        fontWeight: T.fwSemibold,
        lineHeight: 1.2,
      }}>
        {value}
      </span>
    </div>
  );
}

function PolicyPanel({ baseline, overrideCount, isAdmin }) {
  if (!baseline) return null;

  return (
    <div data-testid="policy-band-expanded">
      {/* 4-column knob grid — 7 cells only; admin link moves to footer */}
      <div style={{
        display:             "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap:                 10,
        padding:             "14px 16px 10px",
        borderBottom:        `1px solid ${T.policyBandBorder}`,
      }}>
        {PARAM_ROWS.map(({ label, key, fmt }) => (
          <KnobCell
            key={key}
            testId={`policy-param-${key}`}
            label={label}
            value={fmt(baseline[key])}
          />
        ))}
      </div>

      {/* Footer: note left, override chip + admin link right */}
      <div style={{
        padding:        "10px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        gap:            12,
        flexWrap:       "wrap",
      }}>
        <span data-testid="policy-band-note">
          These assumptions drive the capacity / over-capacity / seats-needed math below.
        </span>
        <span style={{
          display:    "inline-flex",
          alignItems: "center",
          gap:        8,
          flexWrap:   "wrap",
          flexShrink: 0,
        }}>
          {overrideCount > 0 && (
            <span
              data-testid="policy-param-overrides"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                borderRadius: 6,
                padding:      "2px 10px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   T.blueSoft,
                color:        T.blue,
                whiteSpace:   "nowrap",
              }}
            >
              {overrideCount} {overrideCount === 1 ? "office" : "offices"} override
            </span>
          )}
          {isAdmin && (
            <a
              data-testid="policy-admin-link"
              href="/admin?section=policy"
              onClick={(e) => e.stopPropagation()}
              style={{
                color:          "var(--v2-accent)",
                fontSize:       T.fsSm,
                fontWeight:     T.fwSemibold,
                textDecoration: "none",
                whiteSpace:     "nowrap",
                flexShrink:     0,
              }}
            >
              Manage in Admin →
            </a>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── UnifiedContextStrip ──────────────────────────────────────────────────────

function StrategyContextListOwner({
  directives = [],
  canEdit = false,
  onEditDirective,
  baseline = null,
  overrideCount = 0,
  isAdmin = false,
  masterExpanded = null,
  density = "comfortable",
  markets = [],
}) {
  const [strategyOpen, setStrategyOpen] = useState(() => readSession(SESSION_KEY_STRATEGY));
  const [policyOpen, setPolicyOpen]     = useState(() => readSession(SESSION_KEY_POLICY));

  function setStrategy(value) {
    setStrategyOpen(value);
    try { sessionStorage.setItem(SESSION_KEY_STRATEGY, String(value)); } catch { /* ignore */ }
  }

  function setPolicy(value) {
    setPolicyOpen(value);
    try { sessionStorage.setItem(SESSION_KEY_POLICY, String(value)); } catch { /* ignore */ }
  }

  // masterExpanded is intentionally not synced to the strip's own open/closed
  // state. The Expand/Collapse all control targets market rows only; the
  // Strategy and Policy panes manage their own independent toggle state.

  const nameMap = new Map(
    (markets ?? []).map((m) => [m.id.toLowerCase(), m.name])
  );

  const actNowCount    = (directives ?? []).filter(isActNow).length;
  const directiveCount = (directives ?? []).length;
  const effectiveDateStr = fmtEffectiveDate(baseline?.effectiveDate ?? null);

  const cardStyle = {
    background:   T.white,
    border:       `1px solid ${T.border}`,
    borderRadius: 12,
    boxShadow:    "0 1px 2px rgba(16,36,58,.05)",
    marginBottom: 10,
    overflow:     "hidden",
  };

  const headerBase = {
    display:    "flex",
    alignItems: "center",
    gap:        10,
    padding:    "11px 16px",
    cursor:     "pointer",
    userSelect: "none",
  };

  return (
    <div data-testid="unified-context-strip" style={{ marginBottom: 12 }}>

      {/* ── Strategy card ──────────────────────────────────────────────────── */}
      <div data-testid="strategy-card" style={cardStyle}>

        {/* Strategy card header */}
        <div
          data-testid={strategyOpen ? "unified-strip-strategy-toggle-header" : "unified-strip-strategy-collapsed"}
          role="button"
          tabIndex={0}
          aria-expanded={strategyOpen}
          aria-label={strategyOpen ? "Hide strategy directives" : "Show strategy directives"}
          onClick={() => setStrategy(!strategyOpen)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStrategy(!strategyOpen); } }}
          style={{
            ...headerBase,
            background:   strategyOpen ? T.ragAmberBg : T.white,
            borderBottom: strategyOpen ? `1px solid ${T.border}` : "none",
          }}
        >
          {/* Amber glyph + label */}
          <span style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color:         T.strategyLabelColor,
            flexShrink:    0,
          }}>
            ◆ Strategy
          </span>

          {/* Directive count */}
          <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold, flexShrink: 0 }}>
            · {directiveCount} {directiveCount === 1 ? "directive" : "directives"}
          </span>

          {/* Act-now pill */}
          {actNowCount > 0 && (
            <span
              data-testid="strategy-band-act-now-chip"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                borderRadius: 6,
                padding:      "2px 10px",
                background:   T.ragRedBg,
                color:        T.ragRed,
                flexShrink:   0,
              }}
            >
              ● {actNowCount} act now
            </span>
          )}

          <div style={{ flex: 1 }} />

          {/* Chevron */}
          <PlanExpandCaret isExpanded={strategyOpen} />
        </div>

        {/* Strategy card body */}
        {strategyOpen && (
          <StrategyPanel
            directives={directives ?? []}
            canEdit={canEdit}
            onEditDirective={onEditDirective}
            density={density}
            nameMap={nameMap}
          />
        )}
      </div>

      {/* ── Policy card ────────────────────────────────────────────────────── */}
      <div data-testid="policy-card" style={{ ...cardStyle, marginBottom: 0 }}>

        {/* Policy card header */}
        <div
          data-testid={policyOpen ? "unified-strip-policy-toggle-header" : "unified-strip-policy-collapsed"}
          role="button"
          tabIndex={0}
          aria-expanded={policyOpen}
          aria-label={policyOpen ? "Hide policy assumptions" : "Show policy assumptions"}
          onClick={() => setPolicy(!policyOpen)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPolicy(!policyOpen); } }}
          style={{
            ...headerBase,
            background:   policyOpen ? T.policyBandBg : T.white,
            borderBottom: policyOpen ? `1px solid ${T.policyBandBorder}` : "none",
          }}
        >
          {/* Blue glyph + label */}
          <span style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color:         "var(--v2-accent)",
            flexShrink:    0,
          }}>
            ◆ Policy
          </span>

          {/* "· Company baseline" muted */}
          <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold, flexShrink: 0 }}>
            · Company baseline
          </span>

          {/* Faint effective date */}
          {effectiveDateStr && (
            <span
              data-testid="policy-band-effective-date"
              style={{ fontSize: T.fsXs, color: T.light, flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Effective {effectiveDateStr}
            </span>
          )}

          <div style={{ flex: 1 }} />

          {/* Chevron */}
          <PlanExpandCaret isExpanded={policyOpen} />
        </div>

        {/* Policy card body */}
        {policyOpen && baseline && (
          <PolicyPanel
            baseline={baseline}
            overrideCount={overrideCount}
            isAdmin={isAdmin}
          />
        )}
      </div>

    </div>
  );
}
